
















































































import type { PropType, Ref } from '@vue/composition-api';
import { watch, onMounted, defineComponent, computed, ref, toRef } from '@vue/composition-api';
import type { ContractLink } from '@getaccept/editor-lib/src/types/models';
import type { ContractListItem, EBlockWidth, MergeValues } from '@getaccept/editor-lib';
import { NodeAlign, ValueAlign } from '@getaccept/editor-lib';
import type { PusherClient } from '@getaccept/pusher';
import { useContractPusher } from '@getaccept/editor-lib/src/components/composables/contract-pusher.composable';
import { useContractLink } from '@getaccept/editor-lib/src/components/composables/contract-link.composable';
import { RecipientStatus } from '@getaccept/lib-shared/src/recipients/types/enums/recipient-status';
import ContractLinkHeader from '@getaccept/editor-lib/src/components/contract-link/ContractLinkHeader.vue';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { ContractHelper as ContractHelperShared } from '@getaccept/editor-lib/src/helpers/contract.helper';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/types/enums/recipient-role';
import EditorNodeWrapper from '../EditorNodeWrapper.vue';
import { ContractHelper } from '../helpers/contract.helper';
import ContractLinkDescriptionInput from './ContractLinkDescriptionInput.vue';
import SignerDetails from './SignerDetails.vue';

export default defineComponent({
  name: 'EditorNodeContractLink',
  components: {
    EditorNodeWrapper,
    SignerDetails,
    ContractLinkHeader,
    ContractLinkDescriptionInput,
  },
  props: {
    node: {
      type: Object as PropType<ContractLink>,
      default: () => ({}),
    },
    dsrTemplateId: { type: String, default: '' },
    mergeValues: { type: Object as PropType<MergeValues>, required: true },
    parentFirstChild: { type: Boolean },
    parentLastChild: { type: Boolean },
    blockWidthClass: { type: String as PropType<EBlockWidth>, default: null },
    rowTypeClass: { type: String, default: null },
    recipient: { type: Object as PropType<Recipient>, default: () => ({}) },
  },
  setup(props) {
    const pusherClient: Ref<PusherClient> = ref(null);
    const {
      pusherSubscribe,
      recipientStatus,
      documentStatus,
      recipientRole,
      newExpirationDate,
    } = useContractPusher(props, pusherClient);
    const contract: Ref<ContractListItem> = ref(null);
    const signingPageUrl: Ref<string> = ref(null);
    const contractSender = computed(() => contract.value?.user);
    const contractLoading = ref(false);
    const senderCompanyName = computed(
      () => props.mergeValues.entity.companyName || props.mergeValues.entity.name
    );

    const getSigningPageUrlForRecipient = (id: string) => {
      const recipientMap = ContractHelper.findMappedRecipient(
        id,
        props.recipient,
        props.node.recipientMaps
      );
      if (!recipientMap) {
        return null;
      }
      return signingPageUrl.value;
    };

    const loadContract = async () => {
      const { contractId } = props.node;
      if (!contractId) {
        return;
      }
      contract.value = await getTokenAndLoadContract(contractId);
    };

    const getTokenAndLoadContract = async (contractId: string) => {
      signingPageUrl.value = await ContractHelper.getSigningPageUrl(contractId);
      if (!signingPageUrl.value) {
        return null;
      }
      const jwt = await ContractHelper.getToken(contractId, signingPageUrl.value);
      pusherClient.value = await ContractHelper.createPusherClient(jwt);
      pusherSubscribe();
      return await ContractHelper.loadContract(jwt);
    };

    const updateRecipientStatus = async ({
      status,
      recipientId,
    }: {
      status: string;
      recipientId: string;
    }) => {
      const recipient = contract.value.recipients.find(recipient => recipient.id === recipientId);
      if (!recipient) {
        const { recipients } = await getTokenAndLoadContract(props.node.contractId);
        contract.value = {
          ...contract.value,
          recipients,
        };
        return;
      }
      contract.value = await ContractHelperShared.updateContractRecipientStatus(
        contract.value,
        recipientId,
        status
      );
    };

    const loadingContractText = computed(() =>
      t('connecting_contract_to_room', {
        contractName: props.node.name,
      })
    );

    const showContractDescription = computed(() =>
      Boolean(contractDescription.value.content[0]?.content)
    );

    const getPreviousSigner = (index: number) => {
      for (let i = index - 1; i >= 0; i--) {
        const recipient = filteredRecipients.value[i];
        if (recipient?.role !== RecipientRole.CC) {
          return recipient;
        }
      }
    };

    onMounted(async () => {
      contractLoading.value = true;
      await loadContract();
      contractLoading.value = false;
    });

    const {
      updateDocumentStatus,
      updateRecipientRole,
      filteredRecipients,
      translatedRole,
      contractStatus,
      showContractDetails,
      contractExpired,
      contractDescription,
    } = useContractLink(contract, toRef(props, 'node'));

    watch(recipientStatus, () => updateRecipientStatus(recipientStatus.value));

    watch(documentStatus, () => updateDocumentStatus(documentStatus.value));

    watch(recipientRole, () => updateRecipientRole(recipientRole.value));

    watch(newExpirationDate, () => loadContract());

    return {
      contract,
      contractDescription,
      contractExpired,
      contractLoading,
      senderCompanyName,
      contractSender,
      contractStatus,
      filteredRecipients,
      getPreviousSigner,
      getSigningPageUrlForRecipient,
      updateRecipientStatus,
      loadingContractText,
      NodeAlign,
      showContractDescription,
      showContractDetails,
      signingPageUrl,
      translatedRole,
      getTokenAndLoadContract,
      loadContract,
      ValueAlign,
      RecipientStatus,
    };
  },
});
